import React, { Fragment, useState } from "react";
import { Order } from "@/types/order";
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useOrders } from "./hooks";
import Header from "../table-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import storage from "@/utils/storage";
import SideBar from "../table-sidebar";
import { TableStates } from "../hooks";
import { getAuthenticatedPages } from "@/utils/helpers";

interface Props {
  data: Order[];
  tableStates: TableStates;
}

const Orders: React.FC<Props> = ({ data: orders, tableStates }) => {
  const isBrandPage = getAuthenticatedPages().brand;
  const {
    view,
    setView,
    sidebarOpen,
    setSidebarOpen,
    selectedOrder,
    setSelectedOrder,
  } = tableStates;

  const { columns, filteredOrders } = useOrders(orders);

  const handleRowClick = (order: Order) => {
    setSelectedOrder(order);
    setSidebarOpen(true);
  };

  const statusFilter = storage.getItem(
    `${isBrandPage ? "brand_orders_filter" : "orders_filter"}`
  )?.status;

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    statusFilter ? [{ id: "status", value: statusFilter }] : []
  );

  const table = useReactTable({
    data: filteredOrders,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: {
      columnFilters,
    },
  });

  return (
    <div>
      <Header table={table} setView={setView} view={view} />
      <Table>
        <TableHeader className="border-b border-slate-200">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="h-12">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <TableRow
                  onClick={() => handleRowClick(row.original)}
                  data-state={row.getIsSelected() && "selected"}
                  className="h-12 cursor-pointer transition-colors duration-200 ease-in-out hover:bg-slate-100"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{
                        width: cell.column.getSize(),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <SideBar
        isOpen={sidebarOpen}
        data={selectedOrder}
        onClose={() => setSidebarOpen(false)}
        view={view}
      />
    </div>
  );
};

export default Orders;
